import CloseIcon from '@mui/icons-material/Close';
import {
  Autocomplete,
  Chip,
  styled,
  TextField,
} from '@mui/material';
import type { AutocompleteProps } from '@mui/material/Autocomplete';
import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';

const StyledAutocomplete = styled(Autocomplete)<AutocompleteProps<string, true, true, true>>(
  () => ({
    '& .MuiChip-root': {
      height: 24,
      fontSize: '0.75rem',
      margin: 2,
      borderRadius: 12,
    },
    '& .MuiInputBase-root': { padding: '2px 4px 6px' },
  })
);

export interface ChipArrayFieldProps {
  /** RHF field name - omit when using controlled mode */
  name?: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  rules?: Parameters<typeof Controller>[0]['rules']; // RHF rules type
  /** Validator for each token (defaults to "anything") */
  validateInput?: (txt: string) => boolean;
  /** Controlled mode props (when *not* using RHF) */
  value?: string[];
  onChange?: (vals: string[]) => void;
  error?: boolean;
  helperText?: string;
}

export default function ChipArrayField({
  name,
  label,
  placeholder = 'Add item',
  disabled = false,
  rules,
  validateInput = () => true,
  value,
  onChange,
  error,
  helperText,
}: ChipArrayFieldProps) {
  const { control } = useFormContext();

  /** Utility shared by both code paths */
  const filterValid = React.useCallback(
    (vals: string[]) => Array.from(new Set(vals.filter(validateInput))),
    [validateInput]
  );

  /* ---------- 1. CONTROLLED MODE (no RHF) ---------- */
  if (value !== undefined && onChange) {
    return (
      <StyledAutocomplete
        multiple
        freeSolo
        options={[]}
        value={value}
        disabled={disabled}
        onChange={(_, newVals) => onChange(filterValid(newVals as string[]))}
        renderInput={(params) => (
          <TextField
            // eslint-disable-next-line react/jsx-props-no-spreading
            {...params}
            variant="standard"
            label={label}
            placeholder={disabled ? '' : placeholder}
            error={error}
            helperText={helperText}
            inputProps={{
              // eslint-disable-next-line react/jsx-props-no-spreading
              ...params.inputProps,
              style: { fontSize: '0.75rem' },
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          (tagValue as string[]).map((opt, idx) => (
            <Chip 
              key={opt} 
              label={opt} 
              size="small" 
              aria-label={opt}
              deleteIcon={<CloseIcon fontSize="small" aria-label={`Remove ${opt}`} />}
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...getTagProps({ index: idx })}
            />
          ))
        }
      />
    );
  }

  /* ---------- 2. RHF-CONTROLLED MODE ---------- */
  if (!name) {
    throw new Error('ChipArrayField: pass either (name) for RHF or (value & onChange) for plain mode');
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => (
        <StyledAutocomplete
          multiple
          freeSolo
          options={[]}
          value={field.value ?? []}
          disabled={disabled}
          onChange={(_, newVals) => field.onChange(filterValid(newVals as string[]))}
          renderInput={(params) => (
            <TextField
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...params}
              variant="standard"
              label={label}
              placeholder={disabled ? '' : placeholder}
              error={error ?? Boolean(fieldState.error)}
              helperText={helperText ?? (fieldState.error?.message as string)}
              inputProps={{
                // eslint-disable-next-line react/jsx-props-no-spreading
                ...params.inputProps,
                style: { fontSize: '0.75rem' },
              }}
            />
          )}
          renderTags={(tagValue, getTagProps) =>
            (tagValue as string[]).map((opt, idx) => (
              <Chip 
                key={opt} 
                label={opt} 
                size="small" 
                aria-label={opt}
                deleteIcon={<CloseIcon fontSize="small" aria-label={`Remove ${opt}`} />}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...getTagProps({ index: idx })} 
              />
            ))
          }
        />
      )}
    />
  );
}
